import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, AlertTriangle, Info, X, Brain } from 'lucide-react';

// Toast Context
const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Toast Component
const Toast = ({ toast, onRemove, index }) => {
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'ai':
        return <Brain className="w-5 h-5 text-purple-500" />;
      default:
        return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
  };

  const getBgColor = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800';
      case 'error':
        return 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800';
      case 'info':
        return 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800';
      case 'ai':
        return 'bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-800';
      default:
        return 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800';
    }
  };

  const getTextColor = () => {
    switch (toast.type) {
      case 'success':
        return 'text-green-800 dark:text-green-200';
      case 'error':
        return 'text-red-800 dark:text-red-200';
      case 'info':
        return 'text-blue-800 dark:text-blue-200';
      case 'ai':
        return 'text-purple-800 dark:text-purple-200';
      default:
        return 'text-green-800 dark:text-green-200';
    }
  };

  // Calculate position - center horizontally, stack vertically
  const topOffset = 20 + (index * 80); // 80px spacing between toasts

  return (
    <div
      className={`
        fixed left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4
        ${getBgColor()}
        border rounded-lg shadow-lg
        transition-all duration-300 ease-in-out
        ${toast.isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
      `}
      style={{ 
        top: `${topOffset}px`,
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-3 w-0 flex-1">
            <p className={`text-sm font-medium ${getTextColor()}`}>
              {toast.message}
            </p>
            {toast.subtitle && (
              <p className={`text-xs mt-1 opacity-75 ${getTextColor()}`}>
                {toast.subtitle}
              </p>
            )}
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className={`inline-flex ${getTextColor()} hover:opacity-75 focus:outline-none`}
              onClick={() => onRemove(toast.id)}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'success', duration = 4000, subtitle = null) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      message,
      type,
      subtitle,
      isVisible: false
    };

    setToasts(prev => [...prev, newToast]);

    // Trigger animation after a brief delay
    setTimeout(() => {
      setToasts(prev => 
        prev.map(toast => 
          toast.id === id ? { ...toast, isVisible: true } : toast
        )
      );
    }, 100);

    // Auto remove after duration
    setTimeout(() => {
      removeToast(id);
    }, duration);

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => 
      prev.map(toast => 
        toast.id === id ? { ...toast, isVisible: false } : toast
      )
    );

    // Remove from array after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300);
  }, []);

  // Enhanced methods with better messaging
  const showSuccess = useCallback((message, duration = 4000, subtitle = null) => {
    return showToast(message, 'success', duration, subtitle);
  }, [showToast]);

  const showError = useCallback((message, duration = 5000, subtitle = null) => {
    return showToast(message, 'error', duration, subtitle);
  }, [showToast]);

  const showInfo = useCallback((message, duration = 4000, subtitle = null) => {
    return showToast(message, 'info', duration, subtitle);
  }, [showToast]);

  const showAI = useCallback((message, duration = 4000, subtitle = null) => {
    return showToast(message, 'ai', duration, subtitle);
  }, [showToast]);

  // Sequential toast methods for upload process
  const showUploadSequence = useCallback(async (fileName) => {
    // 1. Upload success
    const uploadId = showSuccess(
      `📄 ${fileName} erfolgreich hochgeladen`,
      3000,
      'Datei wurde gespeichert'
    );

    // 2. Analysis start (after 1 second delay)
    setTimeout(() => {
      showAI(
        `🧠 KI-Analyse gestartet`,
        5000,
        `Gemini analysiert: ${fileName}`
      );
    }, 1000);

    // Note: Completion notification is handled by the actual OCR process
    // No fake "Analysis complete" message here
  }, [showSuccess, showAI]);

  const contextValue = {
    showToast,
    removeToast,
    // Basic methods
    showSuccess,
    showError,
    showInfo,
    showAI,
    // Enhanced methods
    showUploadSequence
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Render toasts */}
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          toast={toast}
          index={index}
          onRemove={removeToast}
        />
      ))}
    </ToastContext.Provider>
  );
};

export default ToastProvider;
